import { BaseTableRow, getHeaderName, TableProps } from "@/types";
import { Box, Typography } from "@mui/material";
import { GridRowParams } from "@mui/x-data-grid";
import { SyntheticEvent } from "react";
import { Table } from "./Table";

interface SimilarJobsTableRow extends BaseTableRow {
  name?: string;
  similarity?: string;
  numberOfMatchingJobAttributes?: number;
  numberOfJobAttributes?: number;
  numberOfOtherJobsJobAttributes?: number;
}

interface SimilarJobsTableProps<
  T extends SimilarJobsTableRow = SimilarJobsTableRow
> extends TableProps<T> {}

export class SimilarJobsTable<T extends SimilarJobsTableRow = SimilarJobsTableRow> extends Table<
  T,
  SimilarJobsTableProps<T>
> {
  static columns = {
    name: {
      field: "name",
      label: "tableColumns.jobTitle"
    },
    similarity: {
      field: "similarity",
      label: "tableColumns.similarityDetails"
    }
  };

  static defaultProps = {
    ...Table.defaultProps,
    noRowsMessage: "No rows, yet"
  };

  constructor(props: SimilarJobsTableProps<T>) {
    super(props);

    this.state = {
      ...this.state
    };
  }

  calculateSimilarity(matching: number, total: number, otherTotal: number) {
    const percentage = (matching / total) * 100;
    return {
      value: percentage,
      display: `${total} → ${otherTotal} = ${matching} (${percentage.toFixed(0)}%)`
    };
  }
  onRowClick(params: GridRowParams<T>, e: SyntheticEvent) {
    const { onJobSelect } = this.props;
    if (onJobSelect) {
      onJobSelect(e, params.row);
    }
  }

  initColumns() {
    const { columns, sortable } = this.props;

    this.columns = [];

    columns?.forEach((c) => {
      if (this.hasColumn(c)) {
        if (c.type === SimilarJobsTable.columns.name) {
          this.columns.push({
            field: "name",
            headerName: getHeaderName(c, this.props.translate),
            sortable: sortable,
            filterable: false,
            disableReorder: true,
            flex: 1.8,
            valueGetter: (_value, row) => row.name,
            renderCell: (params) => <div className='truncate'>{params.row.name}</div>
          });
        }
        if (c.type === SimilarJobsTable.columns.similarity) {
          this.columns.push({
            field: "similarity",
            headerName: getHeaderName(c, this.props.translate),
            sortable: sortable,
            filterable: false,
            disableReorder: true,
            flex: 1,
            valueGetter: (_value, row) => {
              return this.calculateSimilarity(
                row.numberOfMatchingJobAttributes || 0,
                row.numberOfJobAttributes || 0,
                row.numberOfOtherJobsJobAttributes || 0
              ).value;
            },
            renderCell: (params) => {
              const similarity = this.calculateSimilarity(
                params.row.numberOfMatchingJobAttributes || 0,
                params.row.numberOfJobAttributes || 0,
                params.row.numberOfOtherJobsJobAttributes || 0
              );

              return (
                <Box display='flex' alignItems='center' gap={1} flex={1}>
                  <Typography variant='body2'>{similarity.display}</Typography>
                </Box>
              );
            }
          });
        }
      }
    });
  }
}
